/**
 * The incoming General Assembly roster — Documentation/ELECTION_OUTCOMES_PRD.md §5.14.
 *
 * Between results day and convene day, the next session's roster is pre-built:
 * members-elect are 'Elect' and holdover senators 'Continuing'. On convene day they
 * become 'Active' on the same rows, so tracking made in December carries into
 * the session with no user action.
 */
import { useQuery } from '@tanstack/react-query';
import api from '../../lib/api';

export interface IncomingChamber {
  seats: number;
  elect: number;
  continuing: number;
  undecided: number;
}

export interface IncomingRoster {
  sessionKey: string;
  label: string;
  shortLabel: string;
  conveneDate: string | null;
  chambers: Partial<Record<'House' | 'Senate', IncomingChamber>>;
}

/** The incoming roster summary, or null when there is none. */
export function useIncomingRoster() {
  return useQuery<IncomingRoster | null>(
    ['legislators', 'incoming'],
    async () => {
      const response = await api.get('/legislators/incoming');
      const data = response.data?.data;
      return data && typeof data.sessionKey === 'string' ? (data as IncomingRoster) : null;
    },
    { staleTime: 5 * 60 * 1000 }
  );
}

/** The roster status of a row, whichever shape it arrives in. */
const rosterStatusOf = (row: Record<string, any>): string | null =>
  row.rosterStatus ?? row.roster_status ?? row.status ?? null;

/**
 * The label an incoming row carries: "Representative-elect" / "Senator-elect", or
 * "Continuing". Null for a seated row.
 */
export function incomingLabel(row: Record<string, any>): string | null {
  const status = rosterStatusOf(row);
  if (status === 'Elect') return row.chamber === 'Senate' ? 'Senator-elect' : 'Representative-elect';
  if (status === 'Continuing') return 'Continuing';
  return null;
}
